import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaShippingFast } from "react-icons/fa";
import Navbar from "../Components/Navbar";
import useOrders from "../hooks/useOrder";
import useAddress from "../hooks/useAddress";

const Order = ({ userId }) => {
  const [activeTab, setActiveTab] = useState("To Pay");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const statusMapping = {
    "To Pay": "to pay",
    "To Receive": "to receive",
    "To Rate": "to rate",
    "Completed": "completed",
    "Returns": "returns",
  };

  const { orders: fetchedOrders = [], updateOrder } = useOrders(
    userId,
    statusMapping[activeTab]
  );

  const { addresses } = useAddress(userId);

  useEffect(() => {
    setOrders(fetchedOrders);
  }, [fetchedOrders]);

  const filteredOrders = orders.filter(
    (o) => o.status?.toLowerCase() === statusMapping[activeTab]
  );

  // SELECT PRODUCT
  const handleSelect = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id)
        ? prev.filter((p) => p !== id)
        : [...prev, id]
    );
  };

  // UPDATE QUANTITY
  const updateQuantity = (productId, type) => {
    setOrders((prev) =>
      prev.map((order) => ({
        ...order,
        products: order.products.map((p) => {
          if (p.id === productId) {
            let qty = p.quantity;

            if (type === "inc") qty += 1;
            if (type === "dec" && qty > 1) qty -= 1;

            return { ...p, quantity: qty };
          }
          return p;
        }),
      }))
    );
  };

  // TOTAL
  const calculateTotal = () => {
    return filteredOrders.reduce((total, order) => {
      return (
        total +
        order.products.reduce((sum, p) => {
          if (
            activeTab === "To Pay" &&
            !selectedProducts.includes(p.id)
          )
            return sum;

          return sum + p.price * p.quantity;
        }, 0)
      );
    }, 0);
  };

  // ADDRESS
  const getAddress = () => {
    const addr = addresses.find((a) => a.user_id === userId);
    return addr
      ? `${addr.location.home}, ${addr.location.barangay}, ${addr.location.municipality}`
      : "No address";
  };

  // CANCEL ORDER
  const handleCancelOrder = async () => {
    try {
      for (let order of filteredOrders) {
        for (let product of order.products) {
          if (selectedProducts.includes(product.id)) {
            await updateOrder({
              id: order.id,
              product_id: product.id,
              status: "cancelled",
            });
          }
        }
      }

      alert("Order cancelled!");

      setOrders((prev) =>
        prev.map((o) => ({
          ...o,
          products: o.products.filter(
            (p) => !selectedProducts.includes(p.id)
          ),
        }))
      );

      setSelectedProducts([]);
    } catch (err) {
      console.error(err);
      alert("Failed to cancel order");
    }
  };

  // CHECKOUT
  const handleCheckout = async () => {
    try {
      for (let order of filteredOrders) {
        for (let product of order.products) {
          if (selectedProducts.includes(product.id)) {
            await updateOrder({
              id: order.id,
              product_id: product.id,
              status: "to receive",
            });
          }
        }
      }

      alert("Checkout successful!");

      setOrders((prev) =>
        prev.map((o) => ({
          ...o,
          status: "to receive",
        }))
      );

      setSelectedProducts([]);
    } catch (err) {
      console.error(err);
      alert("Checkout failed");
    }
  };

  return (
    <>
      <Navbar activeSection="marketplace" />

      <Container>
        {/* TABS */}
        <Tabs>
          {Object.keys(statusMapping).map((tab) => (
            <Tab
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </Tab>
          ))}
        </Tabs>

        <Layout>
          {/* LEFT SIDE */}
          <Left>
            {filteredOrders.length === 0 ? (
              <Empty>No orders found.</Empty>
            ) : (
              filteredOrders.map((order) =>
                order.products.map((p) => (
                  <Card key={p.id}>
                    <LeftSide>
                      {activeTab === "To Pay" && (
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(p.id)}
                          onChange={() => handleSelect(p.id)}
                        />
                      )}
                      <Image src={p.image} />
                    </LeftSide>

                    <Center>
                      <Title>{p.product_name}</Title>
                      <Details>₱{p.price}</Details>

                      {/* QUANTITY */}
                      <QuantityBox>
                        <QtyBtn onClick={() => updateQuantity(p.id, "dec")}>
                          -
                        </QtyBtn>
                        <QtyValue>{p.quantity}</QtyValue>
                        <QtyBtn onClick={() => updateQuantity(p.id, "inc")}>
                          +
                        </QtyBtn>
                      </QuantityBox>

                      <Address>
                        <FaShippingFast /> {getAddress()}
                      </Address>
                    </Center>

                    <Right>
                      <Price>
                        ₱{(p.price * p.quantity).toFixed(2)}
                      </Price>
                    </Right>
                  </Card>
                ))
              )
            )}
          </Left>

          {/* RIGHT SIDE */}
          {activeTab === "To Pay" && (
            <RightPanel>
              <Summary>
                <h3>Order Summary</h3>

                <Row>
                  <span>Selected Items</span>
                  <span>{selectedProducts.length}</span>
                </Row>

                <Row>
                  <span>Total</span>
                  <Total>
                    ₱{calculateTotal().toFixed(2)}
                  </Total>
                </Row>

                <Checkout onClick={handleCheckout}>
                  Proceed to Checkout
                </Checkout>

                <Cancel onClick={handleCancelOrder}>
                  Cancel Order
                </Cancel>
              </Summary>
            </RightPanel>
          )}
        </Layout>
      </Container>
    </>
  );
};

export default Order;

//
// 🎨 STYLES
//

const Container = styled.div`
  padding: 20px;
  background: #f4f7f2;
  min-height: 100vh;
`;

const Tabs = styled.div`
  display: flex;
  gap: 10px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  background: ${({ active }) => (active ? "#28a745" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#333")};
`;

const Layout = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const RightPanel = styled.div`
  flex: 1;
`;

const Card = styled.div`
  display: flex;
  padding: 15px;
  background: white;
  border-radius: 12px;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Center = styled.div`
  flex: 1;
  margin-left: 15px;
`;

const Right = styled.div`
  text-align: right;
`;

const Image = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  object-fit: cover;
`;

const Title = styled.h3`
  margin: 0;
`;

const Details = styled.p`
  color: #777;
  margin: 5px 0;
`;

const Address = styled.p`
  font-size: 0.85rem;
  color: #555;
`;

const Price = styled.h3`
  color: #28a745;
`;

const QuantityBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QtyBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: #28a745;
  color: white;
  border-radius: 5px;
  cursor: pointer;
`;

const QtyValue = styled.span`
  font-weight: bold;
`;

const Summary = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
`;

const Total = styled.h2`
  color: #28a745;
`;

const Checkout = styled.button`
  width: 100%;
  padding: 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  margin-top: 15px;
  cursor: pointer;
`;

const Cancel = styled.button`
  width: 100%;
  padding: 10px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  margin-top: 10px;
  cursor: pointer;
`;

const Empty = styled.p`
  text-align: center;
  color: #777;
`;